import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SelectInput,
  ApiErrorText,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Loader,
} from '@/shared/components';
import { getRequiredLabel } from '@/shared/lib';
import type { WithdrawSbpFormValues } from '../model';
import type { UseFormReturn } from 'react-hook-form';
import type { SbpBankOption } from '../model/types';
import type { FiatWithdrawAssetOption } from '@/entities/operation';
import {
  WithdrawAmountFormField,
  WithdrawIncludeFeeFormField,
} from '@/entities/operation';

interface WithdrawSbpFormProps {
  form: UseFormReturn<WithdrawSbpFormValues>;
  watchedValues: WithdrawSbpFormValues;
  assetOptions: FiatWithdrawAssetOption[];
  selectedAsset?: FiatWithdrawAssetOption;
  bankOptions: SbpBankOption[];
  selectedBank?: SbpBankOption;
  hasWithdrawFee: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: unknown | null;
  onMaxClick: () => void;
  onFormDirtyChange: (isDirty: boolean) => void;
}

export function WithdrawSbpForm({
  form,
  assetOptions,
  selectedAsset,
  bankOptions,
  hasWithdrawFee,
  isLoading,
  isError,
  error,
  onMaxClick,
  onFormDirtyChange,
  watchedValues,
}: WithdrawSbpFormProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const formValues = form.getValues();
    const hasAnyFieldFilled = Object.values(formValues).some(value => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      return value;
    });

    onFormDirtyChange(hasAnyFieldFilled);
  }, [watchedValues, onFormDirtyChange, form]);

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ApiErrorText error={error} size="small" />;
  }

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form className="flex flex-col gap-[1.6rem]">
          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <SelectInput
                    {...field}
                    floatingLabel={getRequiredLabel(
                      t('withdrawMethods.sbp.assetLabel')
                    )}
                    options={assetOptions}
                    valueKey="code"
                    selectedKey="code"
                    disabled={assetOptions.length <= 1}
                  />
                </FormControl>
                <FormMessage className="px-[1.6rem]" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    disabled
                    className="p3"
                    label={getRequiredLabel(
                      t('withdrawMethods.sbp.phoneLabel')
                    )}
                  />
                </FormControl>
                <FormMessage className="px-[1.6rem]" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="bankId"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <SelectInput
                    {...field}
                    floatingLabel={getRequiredLabel(
                      t('withdrawMethods.sbp.bankLabel')
                    )}
                    options={bankOptions}
                    valueKey="id"
                    labelKey="name"
                    disabled={bankOptions.length <= 1}
                  />
                </FormControl>
                <FormMessage className="px-[1.6rem]" />
              </FormItem>
            )}
          />

          <WithdrawAmountFormField
            control={form.control}
            name="amount"
            selectedAsset={selectedAsset}
            onMaxClick={onMaxClick}
            availableBalanceTranslationKey="withdrawMethods.card.availableBalance"
          />

          <WithdrawIncludeFeeFormField
            control={form.control}
            name="includeFee"
            visible={hasWithdrawFee}
          />
        </form>
      </Form>
    </div>
  );
}
